import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Drawer from '../../ui/Drawer'
import Button from '../../ui/Button'
import Chip from '../../ui/Chip'
import SearchInput from '../../ui/SearchInput'
import FormSelect from '../../ui/Select/FormSelect'
import ConfirmDialog from '../../ui/ConfirmDialog'
import ConditionCard from './ConditionCard'
import ConditionForm from './ConditionForm'
import SectionGroup from './SectionGroup'
import {
  activeRules, emptyRule, duplicateRule, isPersisted, groupBySections, ruleSectionId,
  findQuestionLabel, sourceLabel, conditionValueLabel, ruleForQuestionSource
} from './helpers'
import { pluralize } from '../../../utils/textHelpers'

const DELETE_MESSAGE = 'Cette condition sera supprimée. Les questions et réponses qui en dépendent '
  + 'ne seront plus affectées par cette règle.'

const ABANDON_MESSAGE = 'La condition en cours d\'édition ne sera pas enregistrée.'
  + ' Vos autres conditions sont conservées.'

const TITLE_CLASS = 'tw:text-base! tw:font-medium! tw:m-0! tw:text-gray-900! tw:leading-none! tw:truncate'

const SEPARATOR = 'tw:w-full tw:border-0 tw:border-t tw:border-solid tw:border-gray-200! tw:m-0!'

const EMPTY_MESSAGE = 'Aucune condition pour le moment. Ajoutez une condition pour adapter '
  + 'le comportement des questions ou de vos variables.'

const SECTION_OPTION_BADGE = 'tw:shrink-0 tw:rounded! tw:bg-gray-100 tw:px-1.5 tw:py-0.5 '
  + 'tw:text-xs tw:font-medium tw:text-gray-700'

const SECTION_EMPTY_WRAP = 'tw:flex tw:flex-col tw:items-center tw:gap-4 tw:w-full tw:text-center'
const SECTION_EMPTY_ICON = 'tw:flex tw:items-center tw:justify-center tw:shrink-0 '
  + 'tw:w-10 tw:h-10 tw:rounded-lg! tw:bg-gray-100'

const countLabel = count => pluralize(count, 'condition')

const renderSectionOption = (option, meta) => {
  if (meta.context !== 'menu')
    return option.label
  return (
    <span className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:w-full">
      <span className="tw:truncate">{option.label}</span>
      <span className={SECTION_OPTION_BADGE}>{countLabel(option.count || 0)}</span>
    </span>
  )
}

const renderCountBadge = count =>
  count > 0 ? <Chip variant="neutral">{countLabel(count)}</Chip> : null

const withSeparators = nodes =>
  nodes.flatMap((node, index) =>
    index === 0 ? [node] : [<hr key={`sep-${index}`} className={`${SEPARATOR} tw:shrink-0`} />, node])

const sectionFilterOption = (sections, sectionId) => {
  if (!sectionId)
    return null
  const match = (sections || []).find(item => String(item.id) === String(sectionId))
  return match ? { value: String(match.id), label: match.name || '(Section sans nom)' } : null
}

const removeRule = (rules, uniqueId) =>
  rules
    .map(rule => rule.uniqueId === uniqueId && isPersisted(rule) ? { ...rule, shouldDestroy: true } : rule)
    .filter(rule => !(rule.uniqueId === uniqueId && !isPersisted(rule)))

const searchText = (rule, number, refs) => {
  const { sections, variables } = refs
  const parts = [`condition n°${number}`]
  rule.conditions.filter(condition => !condition.shouldDestroy).forEach(condition => {
    parts.push(sourceLabel(sections, variables, condition))
    parts.push(conditionValueLabel(refs, condition))
  })
  rule.rule_actions.filter(action => !action.shouldDestroy).forEach(action => {
    parts.push(findQuestionLabel(sections, action.targetable_id))
  })
  return parts.join(' ').toLowerCase()
}

const ConditionsDrawer = ({
  isOpen, onClose, rules, onChange, sections, variables, gradeConfig,
  initialSectionFilterId, initialQuestionId
}) => {
  const [search, setSearch] = useState('')
  const [sectionFilter, setSectionFilter] = useState(null)
  const [collapsedSections, setCollapsedSections] = useState(() => new Set())
  const [editing, setEditing] = useState(null)
  const [confirmingClose, setConfirmingClose] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const bottomRef = useRef(null)
  const prevOpenRef = useRef(false)
  const consumedQuestionRef = useRef(null)
  const [scrollToId, setScrollToId] = useState(null)
  const isEditing = Boolean(editing)

  const activeList = activeRules(rules)
  const editingId = editing?.uniqueId
  const isNewEditing = Boolean(editing) && !rules.some(rule => rule.uniqueId === editingId)
  const term = search.trim().toLowerCase()

  const matches = (rule, number) =>
    term === '' || searchText(rule, number, { sections, variables, gradeConfig }).includes(term)

  const groups = groupBySections(sections, activeList)
  const conditionCountBySection = new Map(
    groups.map(group => [String(group.sectionId), group.entries.length])
  )
  const sectionOptions = (sections || [])
    .map(section => ({
      value: String(section.id),
      label: section.name || '(Section sans nom)',
      count: conditionCountBySection.get(String(section.id)) || 0
    }))
  const visibleGroups = groups
    .filter(group => !sectionFilter || String(group.sectionId) === sectionFilter.value
      || group.entries.some(entry => entry.rule.uniqueId === editingId))
    .map(group => ({
      ...group,
      entries: group.entries.filter(entry =>
        entry.rule.uniqueId === editingId || matches(entry.rule, entry.number))
    }))
    .filter(group => term === '' || group.entries.length > 0)

  const filtered = Boolean(sectionFilter)
  const isFirstAdd = activeList.length === 0 && isNewEditing
  const filterSectionId = sectionFilter?.value || null
  const newInSection = isNewEditing && filtered
  const filterSection = filtered ? (sections || []).find(item => String(item.id) === filterSectionId) : null
  const hasFilterGroup = visibleGroups.some(group => String(group.sectionId) === filterSectionId)

  let renderableGroups = visibleGroups
  if (newInSection && filterSection && !hasFilterGroup) {
    const emptyGroup = {
      sectionId: Number(filterSectionId),
      name: filterSection.name || '(Section sans nom)',
      entries: []
    }
    renderableGroups = [...visibleGroups, emptyGroup]
  }

  useEffect(() => {
    if (newInSection && bottomRef.current)
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [newInSection])

  useEffect(() => {
    if (isOpen)
      setSectionFilter(sectionFilterOption(sections, initialSectionFilterId))
  }, [isOpen, initialSectionFilterId, sections])

  useEffect(() => {
    if (isOpen && !prevOpenRef.current)
      setCollapsedSections(new Set([...(sections || []).map(section => String(section.id)), 'none']))
    prevOpenRef.current = isOpen
  }, [isOpen, sections])

  useEffect(() => {
    if (!isOpen) {
      consumedQuestionRef.current = null
      return
    }
    if (initialQuestionId && consumedQuestionRef.current !== initialQuestionId) {
      consumedQuestionRef.current = initialQuestionId
      const rule = ruleForQuestionSource(sections, initialQuestionId)
      setEditing(rule)
      setScrollToId(rule.uniqueId)
    }
  }, [isOpen, initialQuestionId, sections])

  const handleSectionFilterChange = useCallback(option => setSectionFilter(option), [])

  const toggleSection = sectionKey => () => setCollapsedSections(prev => {
    const next = new Set(prev)
    if (next.has(sectionKey))
      next.delete(sectionKey)
    else
      next.add(sectionKey)
    return next
  })

  const handleEdit = rule => () => setEditing(rule)
  const handleDuplicate = rule => () => onChange([...rules, duplicateRule(rule)])

  const handleDelete = (rule, number) => () => setPendingDelete({ rule, number })

  const cancelDelete = useCallback(() => setPendingDelete(null), [])

  const confirmDelete = useCallback(() => {
    onChange(removeRule(rules, pendingDelete.rule.uniqueId))
    setPendingDelete(null)
  }, [rules, onChange, pendingDelete])

  const handleSearch = useCallback(event => setSearch(event.target.value), [])
  const handleAdd = useCallback(() => setEditing(emptyRule()), [])
  const handleCancel = useCallback(() => setEditing(null), [])

  const handleClose = useCallback(() => {
    if (editing) {
      setConfirmingClose(true)
      return
    }
    onClose()
  }, [editing, onClose])

  const handleAbandon = useCallback(() => {
    setConfirmingClose(false)
    setEditing(null)
    onClose()
  }, [onClose])

  const handleContinue = useCallback(() => setConfirmingClose(false), [])

  const handleSubmit = useCallback(updated => {
    const exists = rules.some(rule => rule.uniqueId === updated.uniqueId)
    let next = [...rules, updated]
    if (exists)
      next = rules.map(rule => rule.uniqueId === updated.uniqueId ? updated : rule)
    onChange(next)
    setEditing(null)
    if (!exists) {
      const sectionId = ruleSectionId(sections, [...updated.conditions, ...updated.rule_actions], null)
      const sectionKey = sectionId === null ? 'none' : String(sectionId)
      setCollapsedSections(prev => {
        const nextCollapsed = new Set(prev)
        nextCollapsed.delete(sectionKey)
        return nextCollapsed
      })
      setScrollToId(updated.uniqueId)
    }
  }, [rules, onChange, sections])

  useEffect(() => {
    if (scrollToId) {
      const element = document.querySelector(`[data-condition-id="${scrollToId}"]`)
      if (element)
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setScrollToId(null)
    }
  }, [scrollToId, rules])

  const renderEntry = ({ rule, number }) => {
    if (rule.uniqueId === editingId)
    {return (
      <ConditionForm
        key={rule.uniqueId}
        initialSectionId={sectionFilter?.value}
        number={number}
        rule={editing}
        sections={sections}
        variables={variables}
        gradeConfig={gradeConfig}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    )}

    if (!matches(rule, number))
      return null

    return (
      <ConditionCard
        key={rule.uniqueId}
        number={number}
        rule={rule}
        sections={sections}
        variables={variables}
        gradeConfig={gradeConfig}
        editDisabled={isEditing}
        onDelete={handleDelete(rule, number)}
        onDuplicate={handleDuplicate(rule)}
        onEdit={handleEdit(rule)}
      />
    )
  }

  const renderNewInline = number => (
    <div key="new-inline" data-condition-id={editing?.uniqueId}>
      <ConditionForm
        initialSectionId={sectionFilter?.value}
        number={number}
        rule={editing}
        sections={sections}
        variables={variables}
        gradeConfig={gradeConfig}
        inline
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    </div>
  )

  const newConditionForm = (
    <div data-condition-id={editing?.uniqueId}>
      <ConditionForm
        initialSectionId={sectionFilter?.value}
        rule={editing}
        sections={sections}
        variables={variables}
        gradeConfig={gradeConfig}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    </div>
  )

  const renderBody = () => {
    if (isFirstAdd)
      return newConditionForm

    return (
      <>
        <FormSelect
          className="tw:shrink-0"
          controlBorderColor="#d1d5db"
          controlMinHeight={43}
          controlPlaceholderColor="#6b7280"
          formatOptionLabel={renderSectionOption}
          options={sectionOptions}
          placeholder="Choisir une section"
          value={sectionFilter}
          menuPortal
          isClearable
          onChange={handleSectionFilterChange}
        />
        <div className="tw:flex tw:gap-2 tw:shrink-0">
          <div className="tw:flex-1 tw:[&_input::placeholder]:text-gray-500!">
            <SearchInput
              name="condition_search"
              placeholder="Rechercher une condition..."
              value={search}
              onChange={handleSearch}
            />
          </div>
          <Button
            variant="primary"
            icon="plus"
            className="tw:whitespace-nowrap"
            disabled={isEditing}
            onClick={handleAdd}
          >
            Ajouter
          </Button>
        </div>
        <hr className={`${SEPARATOR} tw:shrink-0`} />
        {isNewEditing && !filtered ? newConditionForm : null}
        {renderableGroups.map(group => {
          const sectionKey = group.sectionId === null ? 'none' : String(group.sectionId)
          const open = filtered || !collapsedSections.has(sectionKey)
          const isTarget = newInSection && String(group.sectionId) === filterSectionId
          const number = group.entries.length + 1
          const entryNodes = group.entries.map(renderEntry).filter(Boolean)
          let children = entryNodes
          if (isTarget)
            children = [...entryNodes, renderNewInline(number)]
          else if (group.entries.length === 0) {
            children = [
              <div key="section-empty" className={SECTION_EMPTY_WRAP}>
                <div className={SECTION_EMPTY_ICON}>
                  <i className="fas fa-search tw:text-xl tw:text-gray-900" />
                </div>
                <div className="tw:flex tw:flex-col tw:items-center tw:gap-1.5">
                  <p className="tw:m-0! tw:text-sm! tw:font-semibold! tw:text-gray-900">Aucune condition</p>
                  <p className="tw:m-0! tw:text-sm tw:text-gray-500">{EMPTY_MESSAGE}</p>
                </div>
              </div>
            ]
          }
          return (
            <SectionGroup
              key={sectionKey}
              collapsible={!filtered}
              count={isTarget ? number : group.entries.length}
              isOpen={open}
              name={group.name}
              onToggle={toggleSection(sectionKey)}
            >
              {withSeparators(children)}
            </SectionGroup>
          )
        })}
        {newInSection ? <div ref={bottomRef} aria-hidden="true" /> : null}
      </>
    )
  }

  return (
    <>
      <Drawer ariaLabel="Conditions d'affichage" isOpen={isOpen} onClose={handleClose}>
        <Drawer.Header closeVariant="dismiss" onClose={handleClose}>
          <div className="tw:flex tw:items-center tw:gap-2 tw:flex-1 tw:min-w-0">
            <h2 className={TITLE_CLASS}>Conditions</h2>
            {renderCountBadge(activeList.length)}
          </div>
        </Drawer.Header>
        <Drawer.Body className="tw:gap-4!">
          {renderBody()}
        </Drawer.Body>
      </Drawer>
      <ConfirmDialog
        isOpen={confirmingClose}
        title="Abandonner les modifications ?"
        message={ABANDON_MESSAGE}
        confirmLabel="Annuler les modifications"
        confirmVariant="danger"
        cancelLabel="Continuer l'édition"
        onConfirm={handleAbandon}
        onCancel={handleContinue}
        onClose={handleContinue}
      />
      <ConfirmDialog
        isOpen={Boolean(pendingDelete)}
        title={pendingDelete ? `Suppression de la condition n°${pendingDelete.number}` : ''}
        message={DELETE_MESSAGE}
        confirmLabel="Supprimer"
        confirmVariant="danger"
        cancelLabel="Annuler"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        onClose={cancelDelete}
      />
    </>
  )
}

ConditionsDrawer.propTypes = {
  gradeConfig: PropTypes.shape({ gradeMin: PropTypes.number, gradeSize: PropTypes.number }),
  initialQuestionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialSectionFilterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  rules: PropTypes.arrayOf(PropTypes.shape({ uniqueId: PropTypes.string })).isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  variables: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number }))
}

ConditionsDrawer.defaultProps = {
  gradeConfig: null,
  initialQuestionId: null,
  initialSectionFilterId: null,
  variables: []
}

export default ConditionsDrawer
